<template>
  <div
    class="c-image-upload flex flex-col items-start w-full p-field"
    :class="[{ 'p-error': errorMessage }, { 'c-image-upload--disabled': disabled }]"
  >
    <label
      v-if="$slots.label"
      class="c-image-upload-label subtitle-2"
      for="image-upload-input"
      id="image-upload-label"
    >
      <slot name="label" />
    </label>

    <FileUpload
      customUpload
      ref="uploader"
      :accept="accept"
      :maxFileSize="maxFileSize"
      :chooseLabel="chooseLabel"
      :invalidFileSizeMessage="invalidFileSizeMessage"
      :invalidFileTypeMessage="translate('errorMessages.fileType', 'File format not supported')"
      @select="onFileSelected"
      :disabled="disabled"
      :pt="{
        input: {
          id: 'image-upload-input',
        },
      }"
    />
    <div class="c-image-upload-file-container relative w-full mt-2 h-[15rem]">
      <Image :src="imageSrc" class="c-image-upload-image w-full h-full" v-if="imageSrc" />
      <div v-if="processingImage" class="c-image-upload-file-loading">
        <ProgressSpinner />
      </div>
      <div
        v-if="!hasImage"
        class="c-image-upload-drag-message top-0 left-0 absolute h-full w-full justify-center items-center subtitle-2"
      >
        <span>{{ dragMessage }}</span>
      </div>
      <button
        type="button"
        :disabled="disabled"
        :aria-label="
          hasImage
            ? translate('imageSelect.changeImage', 'Change image')
            : translate('imageSelect.selectImage', 'Select image')
        "
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
        @click="callFileUpload"
        :class="[
          'c-drop-zone-border w-full h-full absolute top-0 lef-0 flex items-center justify-center flex-col',
          { 'c-image-upload-filled': hasImage },
          { 'c-image-upload-highlight': dragOver },
          { 'c-image-upload-drop-zone': !imageSrc },
          { 'cursor-pointer': !disabled },
          { 'cursor-not-allowed': disabled },
        ]"
      >
        <template v-if="!imageSrc">
          <CloudUpload class="c-image-upload-icon" aria-hidden="true" />
          <span class="c-image-upload-title">{{ emptyMessage }}</span>
        </template>
      </button>
    </div>

    <div class="w-full flex items-start justify-between gap-4 mt-2">
      <span class="info-message">{{ infoMessage }}</span>
      <Button
        v-if="hasImage"
        :label="translate('imageSelect.removeImage', 'Remove image')"
        :class="`c-button-text shrink-0 ${ButtonStateClass.TERTIARY} ${ButtonSeverityClass.DANGER} ${ButtonSizeClass.SMALL}`"
        icon="pi pi-trash"
        @click="onRemoveClicked"
        :disabled="disabled"
      />
    </div>

    <div class="p-error c-image-upload-error flex items-center gap-1 mt-2" v-if="errorMessage">
      <CircleAlert class="c-upload-error-icon shrink-0" aria-hidden="true" />
      <span>{{ errorMessage }}</span>
    </div>

    <span v-if="$slots.helperText" class="caption">
      <slot name="helperText" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, type PropType, onUnmounted } from 'vue';
import { FileUpload } from './../../../core/file/file-upload';
import { CircleAlert, CloudUpload } from 'lucide-vue-next';
import { Image } from './../../../core/media';
import {
  Button,
  ButtonSeverityClass,
  ButtonSizeClass,
  ButtonStateClass,
} from './../../../core/buttons';
import { useTranslation } from '../../../../../helpers';

const { translate } = useTranslation();

const MAX_FILE_SIZE = 5 * 1000000;

const props = defineProps({
  accept: {
    type: String,
    default: 'image/png, image/jpeg, image/jpg',
  },
  maxFileSize: {
    type: Number,
    default: MAX_FILE_SIZE,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  useS3bucket: {
    type: Boolean,
    default: true,
  },
  beforeSelect: {
    type: Function as PropType<(file: File) => Promise<File>>,
  },
});

const emit = defineEmits(['select', 'removeSrc', 'removeFile', 'fileDropped']);

const uploader = ref(null);
// const selectedFile = ref(null);
const dragOver = ref(false);
const errorMessage = ref(null);

const selectedFile = defineModel<any>('file');
const srcUrl = defineModel<any>('src');

watch(
  () => uploader.value?.messages?.[0],
  (newMessage) => {
    if (newMessage) {
      errorMessage.value = newMessage;
    }
  },
);

const chooseLabel = computed(() =>
  translate(
    `buttons.${selectedFile.value ? 'change' : 'select'}`,
    selectedFile.value ? 'Change' : 'Select',
  ),
);

const hasImage = computed(() =>
  Boolean(selectedFile.value?.objectURL || selectedFile.value || srcUrl.value),
);

const imageSrc = computed(() => {
  return selectedFile.value?.objectURL || validateSelectImage() || srcUrl.value;
});

const validateSelectImage = (): string => {
  return selectedFile.value ? setAWSS3BucketPrefix() : null;
};

const setAWSS3BucketPrefix = (): string => {
  if (props.useS3bucket) {
    return import.meta.env.VITE_AWS_BUCKET + selectedFile.value;
  } else {
    return selectedFile.value;
  }
};

const bytesToNearestSize = (bytes: number): string => {
  if (bytes < 1000) {
    return `${bytes} B`; // Less than 1 KB
  } else if (bytes < 1000 * 1000) {
    return `${bytes / 1000} KB`; // KB
  } else {
    return `${bytes / (1000 * 1000)} MB`; // MB
  }
};

const invalidFileSizeMessage = computed(() => {
  const bitsToString = bytesToNearestSize(props?.maxFileSize);
  return translate(
    'errorMessages.fileSize',
    { maxFileSize: bitsToString },
    'File cannot be bigger than {maxFileSize}',
  );
});

const dragMessage = computed(() =>
  translate('imageSelect.dragMessage', 'Drag and drop .jpg, .jpeg or .png'),
);

const emptyMessage = computed(() => translate('imageSelect.emptyMessage', 'Upload image'));

const infoMessage = computed(() =>
  translate('imageSelect.infoMessage', 'You can upload .jpg, .jpeg or .png'),
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onDragEnter = (event: Event) => {
  event.stopPropagation();
  event.preventDefault();
};

const onDragOver = (event: Event): void => {
  if (!props.disabled) {
    event.stopPropagation();
    event.preventDefault();
    dragOver.value = true;
  }
};

const onDragLeave = (): void => {
  dragOver.value = false;
};

const onDrop = (event: Event): void => {
  if (!props.disabled) {
    event.stopPropagation();
    event.preventDefault();

    dragOver.value = false;
    errorMessage.value = '';

    const files =
      (event as DragEvent).dataTransfer?.files || (event.target as HTMLInputElement).files;

    if (files?.length === 1) {
      uploader.value.onFileSelect(event);
      emit('fileDropped');
    } else {
      errorMessage.value = translate('errorsMessages.fileLimit', 'Only one file can be selected');
    }
  }
};

const processingImage = ref<boolean>(false);

const processImage = async (file: File): Promise<File> => {
  if (!props.beforeSelect) {
    return file;
  }
  try {
    processingImage.value = true;
    const processedFile = await props.beforeSelect(file);
    return processedFile;
  } catch (error) {
    console.error(error);
  } finally {
    processingImage.value = false;
  }
};

const onFileSelected = async ({ files }: { files: any[] }): Promise<void> => {
  const file = files.length ? files[files.length - 1] : null;

  if (file) {
    if (uploader.value?.messages?.length) return;

    const processedImage = await processImage(file);
    selectedFile.value = processedImage;
    errorMessage.value = '';

    if (files.length > 1) {
      uploader.value.remove(0);
    }

    removeSrc();

    emit('select', processedImage);
  }
};

const removeSrc = (): void => {
  if (srcUrl.value) {
    srcUrl.value = null;
    emit('removeSrc');
  }
};

const removeFile = (): void => {
  if (selectedFile.value) {
    selectedFile.value = null;
    emit('removeFile');
  }
};

const onRemoveClicked = (): void => {
  errorMessage.value = null;
  uploader.value.clear();

  removeFile();
  removeSrc();
};

const callFileUpload = (): void => {
  uploader.value.choose();
};

type FileWithObjectURL = File & {
  objectURL: string;
};

const fileHistory: FileWithObjectURL[] = [];

watch(
  selectedFile,
  (newVal: File | undefined | null) => {
    // Check if it's a File AND has the objectURL property
    if (newVal instanceof File && 'objectURL' in newVal) {
      fileHistory.push(newVal as FileWithObjectURL);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  fileHistory.forEach((file) => {
    URL.revokeObjectURL(file.objectURL);
  });
});
</script>

<style lang="scss" scoped>
.cursor-not-allowed {
  cursor: not-allowed !important;
}

:deep(.p-fileupload) {
  .p-fileupload-choose .p-button-icon::before {
    content: '\e934';
  }

  .p-fileupload-content,
  .p-fileupload-buttonbar button.p-button {
    display: none;
  }

  .p-fileupload-buttonbar {
    display: none;
  }
}

.c-image-upload-file-container {
  border-radius: var(--radius-role-md);
  overflow: hidden;
}

.c-drop-zone-border {
  appearance: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: center;
  background-color: transparent;
  border: var(--border-width-default) dashed var(--color-forms-border);
  border-radius: var(--radius-role-md);

  &.c-image-upload-filled {
    border-style: solid;
  }

  &:hover,
  &.c-image-upload-highlight {
    border-color: var(--color-border-strong);
  }

  &:focus-visible {
    outline: none;
    border: var(--border-width-emphasis) solid var(--amber-800);
  }
}

.c-image-upload-drop-zone {
  background-color: var(--color-surface-canvas);
}

.c-image-upload-icon {
  width: var(--size-icon-lg);
  height: var(--size-icon-lg);
  color: var(--color-icon-secondary);
}

.c-image-upload-title {
  margin: var(--space-4) 0 0;
  font-size: var(--typography-body-lg-font-size);
  font-weight: var(--font-weight-500);
  line-height: var(--typography-body-lg-line-height);
  color: var(--color-text-link);
}

.info-message,
.c-image-upload-error {
  font-size: var(--typography-body-font-size);
  line-height: var(--typography-body-line-height);
}

.info-message {
  color: var(--color-text-secondary);
}

.c-image-upload-error {
  color: var(--color-feedback-text-danger);
}

.c-image-upload--disabled {
  .c-image-upload-label,
  .c-image-upload-icon,
  .c-image-upload-title,
  .info-message {
    color: var(--color-disabled-content);
  }

  .c-drop-zone-border {
    border-color: var(--color-forms-border);
  }

  .c-image-upload-drop-zone {
    background-color: var(--color-disabled-surface);
  }
}

.c-image-upload {
  &-image {
    aspect-ratio: 4/3;
    object-fit: cover;
  }

  &-drag-message {
    display: none;
  }

  &-file-loading {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    z-index: 1;
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: var(--color-overlay-scrim);
      z-index: -1;
    }
  }
}
.c-upload-error-icon {
  width: var(--size-icon-sm);
  height: var(--size-icon-sm);
  color: var(--color-feedback-icon-danger);
}
</style>
